@if (globalService.isMobileStyle) {
  <ion-content [fullscreen]="true">
    <ng-container *ngTemplateOutlet="privacyPolicyTemplate"></ng-container>
  </ion-content>
} @else {
  <ng-container *ngTemplateOutlet="privacyPolicyTemplate"></ng-container>
}

<ng-template #privacyPolicyTemplate>
  <main class="privacy-policy-page">
    <div class="privacy-shell">
      <header class="privacy-hero">
        <p class="hero-chip">Green Pulse</p>
        <h1>Privacy Policy</h1>
        <p class="hero-description">
          This policy explains how the application handles access, permissions, and operational data for authorized users.
        </p>
        <div class="effective-date">
          <span>Effective Date</span>
          <strong>March 4, 2026</strong>
        </div>
      </header>

      <section class="quick-glance">
        <article>
          <h3>Access Model</h3>
          <p>Only administrator-managed, authorized users can access the app.</p>
        </article>
        <article>
          <h3>Data Usage</h3>
          <p>Permissions are used only for features that users actively run.</p>
        </article>
        <article>
          <h3>Security Focus</h3>
          <p>Reasonable technical and organizational safeguards are applied.</p>
        </article>
      </section>

      <nav class="section-nav" aria-label="Policy sections">
        <button type="button" (click)="scrollToSection('collection')">Collection</button>
        <button type="button" (click)="scrollToSection('permissions')">Permissions</button>
        <button type="button" (click)="scrollToSection('communications')">Notifications</button>
        <button type="button" (click)="scrollToSection('third-party')">Third Party</button>
        <button type="button" (click)="scrollToSection('security')">Security</button>
        <button type="button" (click)="scrollToSection('children')">Children</button>
        <button type="button" (click)="scrollToSection('changes')">Changes</button>
        <button type="button" (click)="scrollToSection('contact')">Contact</button>
      </nav>

      <section class="privacy-card">
        <article class="policy-block" id="collection">
          <h2>Information Collection and Use</h2>
          <p>
            This Privacy Policy describes how <b>Green Pulse</b> ("the Application") handles information when you use our mobile
            application. The Application is intended for <b>authorized users only</b>. User accounts are created and managed by system
            administrators, and users cannot register themselves through the Application. By using the Application, you agree to the
            practices described in this Privacy Policy.
          </p>
          <p>
            The Application may process and store limited account-related information (such as username, role, and organization) as required
            for system access control and application functionality.
          </p>
        </article>

        <article class="policy-block" id="permissions">
          <h2>Device Permissions</h2>
          <h3>Camera Permission</h3>
          <p>
            The Application requests camera access to scan QR codes or barcodes. The camera is used only when the scanning feature is
            activated by the user. The Application does not store or transmit captured images or videos.
          </p>
          <h3>Flashlight Permission</h3>
          <p>
            The Application may access the device flashlight to improve visibility during QR or barcode scanning in low-light environments.
            This permission is used only during the scanning process.
          </p>
          <h3>Location Information</h3>
          <p>
            Location information is used only for real-time map display within the Application. The Application itself does not store
            location history. However, third-party map service providers may process technical data according to their own privacy policies.
          </p>
        </article>

        <article class="policy-block" id="communications">
          <h2>Notifications and Connectivity</h2>
          <h3>Push Notifications</h3>
          <p>
            The Application uses Firebase Cloud Messaging (FCM) to deliver push notifications for alerts, updates, or important service
            information. Users may disable notifications at any time through device settings.
          </p>
          <h3>Internet Access</h3>
          <p>
            The Application requires internet access to communicate with backend services, retrieve data, and support maps, notifications,
            and system updates.
          </p>
        </article>

        <article class="policy-block" id="third-party">
          <h2>Third-Party Services</h2>
          <p>The Application may use trusted third-party services to support functionality, including:</p>
          <ul>
            <li>Firebase Cloud Messaging (FCM) for push notifications.</li>
            <li>Google Maps Platform for map rendering and location display.</li>
            <li>Google ML Kit for barcode or QR code scanning features.</li>
          </ul>
          <p>
            These services may collect limited technical information needed to deliver their functionality, according to their respective
            privacy policies.
          </p>
        </article>

        <article class="policy-block" id="security">
          <h2>Data & Security</h2>
          <h3>Data Security</h3>
          <p>
            We value your trust and apply reasonable measures to protect information used by the Application. However, no method of
            electronic transmission or storage is completely secure, and absolute security cannot be guaranteed.
          </p>
          <h3>Data Retention</h3>
          <p>
            Account and operational data are retained only as long as necessary to support application functionality and administrative
            requirements. Data may be deleted upon account deactivation, subject to system policies.
          </p>
          <h3>Data Deletion</h3>
          <p>
            Since user accounts are administrator-managed, users who wish to request data deletion should contact their system
            administrator. Administrators may contact us through the contact details below if required.
          </p>
        </article>

        <article class="policy-block" id="children">
          <h2>Children's Privacy</h2>
          <p>
            The Application is not intended for children under the age of 13, and we do not knowingly collect personal information from
            children.
          </p>
        </article>

        <article class="policy-block" id="changes">
          <h2>Changes to This Privacy Policy</h2>
          <p>We may update this Privacy Policy from time to time. Any updates will be posted on this page with a revised effective date.</p>
        </article>

        <article class="policy-block contact-block" id="contact">
          <h2>Contact Us</h2>
          <p>If you have any questions or concerns regarding this Privacy Policy, please contact us.</p>
        </article>
      </section>
    </div>
  </main>
</ng-template>
